import { calculateWorkOrderCosts } from '@calculations/cost/work-order';
import { calculate } from '@calculations/price/strategies/target-contribution-margin/calculate';
import { calculateUnitPriceFromContributionMargin } from '@calculations/price/utilities/contribution-margin';
import { calculateGrossMarginFromUnitPrice } from '@calculations/price/utilities/gross-margin';
import { calculateMarginsFromUnitPrice } from '@calculations/price/utilities/margins';
import { SnapRoundingOptionsCustom } from '@calculations/round';
import { PriceStrategy } from '@snap-types/models/enums';
import { Decimal } from '@snap-types/types/decimal';

import { storeToCalculationSnapWorkOrder } from '../../transforms/store-to-calculation-snap-work-order';
import { transformWorkOrderCostsToStoreState } from '../../utilities/transform-work-order-costs';
import {
  OnContributionMarginChangeInput,
  OnContributionMarginChangeOutput,
  OnQuantityChangeInput,
  OnQuantityChangeOutput,
  OnQuotePriceCreationInput,
  OnQuotePriceCreationOutput,
  OnUnitPriceChangeInput,
  OnUnitPriceChangeOutput,
  RecalculatePriceStrategy,
} from '../types';

function onQuantityChange({
  state,
  quoteLine,
  quotePrice,
  quantity,
  priceStrategyConfigs,
}: OnQuantityChangeInput): OnQuantityChangeOutput {
  if (!quoteLine.snapWorkOrder) {
    return {
      status: 'failure',
      reason: 'No Snap Work Order Found',
    };
  }

  const priceStrategyConfig = priceStrategyConfigs.find(
    (config) => config.priceStrategyType === quotePrice.priceStrategy,
  );

  if (
    !priceStrategyConfig ||
    priceStrategyConfig.priceStrategyType !==
      PriceStrategy.TargetContributionMargin
  ) {
    return {
      status: 'failure',
      reason: 'No price strategy config found',
    };
  }

  const initialContributionMargin =
    quotePrice.contributionMargin ??
    priceStrategyConfig.targetContributionMargin;

  const costs = calculateWorkOrderCosts({
    quantity,
    ...storeToCalculationSnapWorkOrder(quoteLine.snapWorkOrder),
  });

  let targetContributionMargin =
    quotePrice.contributionMargin ??
    priceStrategyConfig.targetContributionMargin;

  const usingCalculatedContributionMargin =
    quotePrice.calculatedContributionMargin &&
    initialContributionMargin.eq(quotePrice.calculatedContributionMargin);

  if (
    usingCalculatedContributionMargin &&
    priceStrategyConfig.targetContributionMargin
  ) {
    targetContributionMargin = priceStrategyConfig.targetContributionMargin;
  }

  const lastWin = quoteLine.lastWin.hasLastWin
    ? {
        contributionMargin: quoteLine.lastWin.contributionMargin as Decimal,
        quantity: quoteLine.lastWin.quantity as Decimal,
        unitPrice: quoteLine.lastWin.unitPrice as Decimal,
      }
    : undefined;

  const { contributionMargin, unitPrice } = calculate(
    {
      quantity,
      unitVariableCosts: costs.unitVariableCosts,
      targetContributionMargin,
      lastWin,
    },
    SnapRoundingOptionsCustom(state),
  );

  const grossMargin = calculateGrossMarginFromUnitPrice({
    unitCost: costs.unitCost,
    unitPrice,
  });

  return {
    status: 'success',
    unitPrice,
    contributionMargin,
    grossMargin,
    ...transformWorkOrderCostsToStoreState(costs),
  };
}

function onUnitPriceChange({
  quotePrice,
  unitPrice,
}: OnUnitPriceChangeInput): OnUnitPriceChangeOutput {
  if (!quotePrice.unitVariableCosts) {
    return {
      status: 'failure',
      reason: 'No variable costs found',
    };
  }

  const margins = calculateMarginsFromUnitPrice({
    unitCost: quotePrice.unitCost,
    unitVariableCosts: quotePrice.unitVariableCosts,
    unitPrice,
  });

  return {
    status: 'success',
    ...margins,
  };
}

function onContributionMarginChange({
  state,
  quotePrice,
  contributionMargin,
}: OnContributionMarginChangeInput): OnContributionMarginChangeOutput {
  if (!quotePrice.unitVariableCosts) {
    return {
      status: 'failure',
      reason: 'No variable costs found',
    };
  }

  const { unitPrice } = calculateUnitPriceFromContributionMargin(
    {
      unitVariableCosts: quotePrice.unitVariableCosts,
      contributionMargin,
    },
    SnapRoundingOptionsCustom(state),
  );

  const grossMargin = calculateGrossMarginFromUnitPrice({
    unitCost: quotePrice.unitCost!,
    unitPrice,
  });

  return {
    status: 'success',
    unitPrice,
    grossMargin,
  };
}

function onQuotePriceCreation({
  state,
  quoteLine,
  quotePrice,
  priceStrategyConfigs,
}: OnQuotePriceCreationInput): OnQuotePriceCreationOutput {
  const { quantity } = quotePrice;

  if (!quoteLine.snapWorkOrder) {
    return {
      status: 'failure',
      reason: 'No Snap Work Order Found',
    };
  }

  const priceStrategyConfig = priceStrategyConfigs.find(
    (config) => config.priceStrategyType === quotePrice.priceStrategy,
  );

  if (
    !priceStrategyConfig ||
    priceStrategyConfig.priceStrategyType !==
      PriceStrategy.TargetContributionMargin
  ) {
    return {
      status: 'failure',
      reason: 'No price strategy config found',
    };
  }

  const targetContributionMargin =
    quotePrice.contributionMargin ??
    priceStrategyConfig.targetContributionMargin;

  const costs = calculateWorkOrderCosts({
    quantity,
    ...storeToCalculationSnapWorkOrder(quoteLine.snapWorkOrder),
  });

  const {
    hasLastWin,
    contributionMargin: lastWinContributionMargin,
    unitPrice: lastWinUnitPrice,
    quantity: lastWinQuantity,
  } = quoteLine.lastWin;

  const hasLastWinData =
    hasLastWin &&
    lastWinContributionMargin &&
    lastWinUnitPrice &&
    lastWinQuantity;

  const lastWin = hasLastWinData
    ? {
        contributionMargin: lastWinContributionMargin,
        quantity: lastWinQuantity,
        unitPrice: lastWinUnitPrice,
      }
    : undefined;

  const { unitPrice, contributionMargin } = calculate(
    {
      quantity,
      unitVariableCosts: costs.unitVariableCosts,
      targetContributionMargin,
      lastWin,
    },
    SnapRoundingOptionsCustom(state),
  );

  return {
    status: 'success',
    quotePrice: {
      ...quotePrice,
      unitPrice,
      contributionMargin,
      ...transformWorkOrderCostsToStoreState(costs),
    },
  };
}

export const recalculateTargetContributionMargin = {
  onQuantityChange,
  onUnitPriceChange,
  onGrossMarginChange: null,
  onContributionMarginChange,
  onBaseGrossMarginChange: null,
  onQuotePriceCreation,
  onUnitCostChange: null,
} satisfies RecalculatePriceStrategy;
